import { useQuery } from '@tanstack/react-query'

import type { ClinicId, ClinicSchedule } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Fetches the per-clinic schedule (grouped by clinic) over a date range in one
 * request. Pass `clinicIds` to restrict to specific clinics (e.g. a region's
 * clinics, or a single one), or omit it for every clinic. Returns the grouped
 * shape directly — used by the admin calendar's rota layer, which works the same
 * whether one or many clinics are present.
 */
export function useClinicSchedules(opts: {
  clinicIds?: ClinicId[]
  from?: string
  to?: string
  includeOverrides?: boolean
  enabled?: boolean
}) {
  const api = useOsApi()
  const { clinicIds, from, to, includeOverrides = true, enabled = true } = opts

  const query = useQuery({
    // 'grouped' discriminates from useClinicSchedule's unwrapped per-clinic entries,
    // which share the 'clinic-schedule' prefix but return a different shape.
    queryKey: ['clinic-schedule', 'grouped', clinicIds ?? 'all', from, to, includeOverrides],
    enabled: enabled && !!from && !!to,
    queryFn: async () => {
      const response = await api.get('/schedules', {
        query: {
          from: from!,
          to: to!,
          clinicIds,
          includeOverrides: includeOverrides ? undefined : 'false',
        },
      })
      if (!response.success) throw new Error(response.error.message)
      return response.data.schedules
    },
  })

  const schedules: ClinicSchedule[] = query.data ?? []
  return { schedules, isLoading: query.isLoading, isError: query.isError }
}
